import { type ComponentType, type FC, useEffect } from 'react';

import { getEntityCurrentAcls } from '../../ducks/common_acls/thunks';
import type { CommonAclsPrefix } from '../../ducks/common_acls/types';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import { defaultPermissions, type PermissionValues } from '../../types/common';
import {
  getEntityUserRoles,
  getPermissionsRolesMerge,
} from '../../utils/get_entity_roles';
import { selectInternalProps } from './selectors';

type PermissionsProps = {
  permissionsRolesMerge: PermissionValues;
  permissions: PermissionValues;
};

const defaultState: PermissionsProps = {
  permissionsRolesMerge: defaultPermissions,
  permissions: defaultPermissions,
};

export type PermissionManagerInjectedProps = Partial<PermissionsProps> & {
  hasPermissions?: boolean;
};

type Props = {
  aclsType: CommonAclsPrefix;
  // `undefined` is used to handle situations when id of an object is not known.
  // Then permissions manager will just render children component without injected props.
  aclsObjectId: string | undefined;
};

// TODO extract this to usePermissions hook
function permissionsManager<T>(
  WrappedComponent: ComponentType<T>,
): ComponentType<T & Props> {
  const WrapperComponent: FC<T & Props> = (props) => {
    const { aclsType, aclsObjectId, ...componentProps } = props;

    const dispatch = useAppDispatch();
    const { permissionsData, userRoles } = useAppSelector((state) =>
      selectInternalProps(state, aclsType),
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: side effect
    useEffect(() => {
      if (!aclsType) {
        throw Error(
          'Prop aclsType must be specified for permissionsManager HOC.',
        );
      }
      if (!permissionsData || !aclsObjectId) {
        return;
      }
      const permissions = permissionsData.entityCurrentUserAcls[aclsObjectId];

      if (!permissions) {
        dispatch(getEntityCurrentAcls(aclsType, aclsObjectId));
      }
    }, [aclsObjectId]);

    if (!permissionsData || !aclsObjectId) {
      return (
        // TypeScript cannot relate `Omit<T, K> & Injected` back to `T` for a
        // generic `T`, which is what a higher-order component reassembles.
        // biome-ignore lint/plugin: see above.
        <WrappedComponent {...(componentProps as T)} hasPermissions={false} />
      );
    }

    let permissionsProps = defaultState;

    const permissions = permissionsData.entityCurrentUserAcls[aclsObjectId];

    if (permissions) {
      const permissionsRolesMerge = getPermissionsRolesMerge(
        aclsType,
        userRoles,
        permissions,
        getEntityUserRoles(userRoles)[aclsType],
      );
      if (permissionsRolesMerge) {
        permissionsProps = {
          permissions,
          permissionsRolesMerge,
        };
      }
    }

    const isReady = permissions !== undefined;

    return (
      <WrappedComponent
        // biome-ignore lint/plugin: see the early return above.
        {...(componentProps as T)}
        {...permissionsProps}
        hasPermissions={isReady}
      />
    );
  };

  return WrapperComponent;
}

export default permissionsManager;
